import { gql, useQuery } from '@apollo/client'
import { GetServerSideProps } from 'next'
import Head from 'next/head'
import { FunctionComponent, useEffect, useState } from 'react'
import { getSessionAuthToken } from '../../../auth/auth0'
import {
  findEntryByPath,
  requestDocumentationBody,
  requestFormattedData,
  requestScopesDescription,
} from '../../../components/developer/helpers'
import { PersonalAccessTokenSection } from '../../../components/developer/tools/personal-access-token'
import { serverLogger } from '../../../utils/serverLogger'
import { IDocsData } from '../../[...id]'

const PersonalAccessTokenPage: FunctionComponent = ({
  data,
  scopesDescription,
  selectedContent,
}: any) => {
  const [isLogged, setIsLogged] = useState(false)

  const tokensQuery = useQuery(TOKENS_QUERY, {
    fetchPolicy: 'cache-and-network',
  })
  const tokensList = tokensQuery.data?.tokens || []

  const docsData: IDocsData = {
    selectedContent,
    list: data,
    path: ['tools', 'personal-access-token'],
  }

  useEffect(() => {
    setIsLogged(!!getSessionAuthToken())
  }, [])

  return (
    <>
      <Head>
        <title>Mapped Developer Portal • Personal Access Token</title>
      </Head>

      <PersonalAccessTokenSection
        data={docsData}
        scopesDescription={scopesDescription}
        tokensList={tokensList}
        isLogged={isLogged}
      />
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async ({
  req,
  resolvedUrl,
}) => {
  const { data, error: formattedDataError } = await requestFormattedData()

  const { data: scopesDescription, error: scopesDescriptionError } =
    await requestScopesDescription()

  const errorWithContext = (e: any) => ({
    error: e,
    req: { resolvedUrl, rawHeaders: req.rawHeaders },
  })

  if (formattedDataError) {
    serverLogger.error(
      '[docs query error]',
      errorWithContext(formattedDataError)
    )
  }

  if (scopesDescriptionError) {
    serverLogger.error(
      '[scopes query error]',
      errorWithContext(scopesDescription)
    )
  }

  if (!data || !scopesDescription) {
    return {
      props: {},
    }
  }

  const selectedContent =
    findEntryByPath(data, ['tools', 'personal-access-token']) || null

  if (selectedContent) {
    const body = await requestDocumentationBody(selectedContent?.uid || '')
    selectedContent.body = body?.data || ''
  }

  return {
    props: {
      selectedContent,
      data,
      scopesDescription,
    },
  }
}

const TOKENS_QUERY = gql`
  query tokens($userId: String) {
    tokens(filter: { userId: { eq: $userId } }) {
      id
      userId
      name
      permissions
      created
      updated
      lastExchanged
    }
  }
`

export default PersonalAccessTokenPage
